import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Input, Modal, Progress, Radio, Spin, Tag, message } from 'antd';
import { LeftOutlined } from '@ant-design/icons';
import api from '../api';
import { getStoredUser, isSuperAdmin } from '../userAuth';
import { H5_TAB_BAR_PX } from '../h5Layout';

const statusMap = {
  0: { text: '待盘点', color: 'gold' },
  1: { text: '盘点中', color: 'blue' },
  2: { text: '已完成', color: 'green' },
};

export default function InventoryTask() {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const user = getStoredUser();
  const [loading, setLoading] = useState(true);
  const [task, setTask] = useState(null);
  const [locations, setLocations] = useState([]);
  const [remarks, setRemarks] = useState([]);
  const [remarkSummary, setRemarkSummary] = useState('');
  const [completeOpen, setCompleteOpen] = useState(false);
  const [feedbackOk, setFeedbackOk] = useState(1);
  const [feedback, setFeedback] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [confirmingId, setConfirmingId] = useState(null);

  const loadRemarks = useCallback(() => {
    return api.get(`/inventory/tasks/${taskId}/remarks`)
      .then((res) => {
        setRemarks(res?.items || []);
        setRemarkSummary(res?.summary || '');
        return res;
      })
      .catch(() => {
        setRemarks([]);
        setRemarkSummary('');
        return null;
      });
  }, [taskId]);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get(`/inventory/tasks/${taskId}`),
      api.get(`/inventory/tasks/${taskId}/locations`),
      loadRemarks(),
    ])
      .then(([t, locs]) => {
        setTask(t);
        setLocations(locs?.items || []);
      })
      .catch((err) => message.error(err.message || '加载失败'))
      .finally(() => setLoading(false));
  }, [taskId, loadRemarks]);

  useEffect(() => { load(); }, [load]);

  const canComplete = task
    && task.status !== 2
    && Number(task.assignee_id) === Number(user?.id);

  const openComplete = async () => {
    const res = await loadRemarks();
    const summary = res?.summary || remarkSummary || '';
    const hasRemarks = (res?.items || remarks || []).length > 0;
    setFeedbackOk(hasRemarks ? 0 : 1);
    if (hasRemarks) {
      setFeedback(summary || '');
    } else {
      setFeedback('全部符合');
    }
    setCompleteOpen(true);
  };

  const handleComplete = async () => {
    if (feedbackOk === 0 && !(feedback || '').trim() && !remarks.length) {
      message.warning('存在问题时请填写备注');
      return;
    }
    const unchecked = Number(task?.unchecked_count || 0);
    if (unchecked > 0 && feedbackOk === 1) {
      const ok = await new Promise((resolve) => {
        Modal.confirm({
          title: '仍有未盘点资产',
          content: `还有 ${unchecked} 件未盘点，确定按「全部符合」提交吗？`,
          okText: '仍要提交',
          cancelText: '返回继续盘',
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        });
      });
      if (!ok) return;
    }
    setSubmitting(true);
    try {
      await api.post(`/inventory/tasks/${taskId}/complete`, {
        feedback_ok: feedbackOk,
        feedback: feedback.trim(),
      });
      message.success('已提交盘点完成反馈');
      setCompleteOpen(false);
      load();
    } catch (err) {
      message.error(err.message || '提交失败');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmRemark = async (remark) => {
    if (!isSuperAdmin(user)) return;
    setConfirmingId(remark.id);
    try {
      await api.put(`/inventory/tasks/${taskId}/remarks/${remark.id}/confirm`);
      message.success('已确认该条备注');
      loadRemarks();
    } catch (err) {
      message.error(err.message || '确认失败');
    } finally {
      setConfirmingId(null);
    }
  };

  const total = Number(task?.total_count || 0);
  const checked = Number(task?.checked_count || 0);
  const percent = total > 0 ? Math.round((checked / total) * 100) : 0;
  const st = statusMap[task?.status] || statusMap[0];
  const unconfirmed = remarks.filter((r) => !r.confirmed).length;

  return (
    <div style={{ minHeight: '100vh', background: '#f5f7fa', paddingBottom: 90 }}>
      <div style={{
        display: 'flex', alignItems: 'center', padding: '12px 16px',
        background: '#fff', borderBottom: '1px solid #f0f0f0', position: 'sticky', top: 0, zIndex: 10,
      }}>
        <LeftOutlined onClick={() => navigate('/inventory')} style={{ fontSize: 18, marginRight: 12 }} />
        <span style={{ fontSize: 17, fontWeight: 600, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {task?.title || '盘点详情'}
        </span>
        <Tag color={st.color}>{st.text}</Tag>
      </div>

      <Spin spinning={loading}>
        <div style={{ margin: 16, background: '#fff', borderRadius: 10, padding: 14 }}>
          <div style={{ fontSize: 13, color: '#666', marginBottom: 10 }}>
            执行人：{task?.assignee_name || '-'}
          </div>
          <Progress percent={percent} size="small" />
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 13, color: '#333' }}>
            <span>已盘点 <b style={{ color: '#1677ff' }}>{checked}</b></span>
            <span>未盘点 <b style={{ color: '#fa8c16' }}>{Number(task?.unchecked_count || 0)}</b></span>
            <span>合计 <b>{total}</b></span>
          </div>
          {task?.status === 2 && (
            <div style={{ marginTop: 12, padding: 10, background: '#f6ffed', borderRadius: 8, fontSize: 13, color: '#389e0d', whiteSpace: 'pre-wrap' }}>
              反馈：{Number(task.feedback_ok) === 1 ? '全部符合' : '存在问题'}
              {task.feedback ? `\n${task.feedback}` : ''}
            </div>
          )}
        </div>

        <div style={{ padding: '0 16px 16px' }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 10, color: '#333' }}>按房间核对</div>
          {locations.length === 0 && !loading ? (
            <div style={{ textAlign: 'center', color: '#999', padding: 40 }}>暂无地点</div>
          ) : (
            locations.map((loc) => {
              const locTotal = Number(loc.total_count || 0);
              const locChecked = Number(loc.checked_count || 0);
              const done = locTotal > 0 && locChecked >= locTotal;
              return (
                <div
                  key={loc.id}
                  onClick={() => navigate(`/inventory/${taskId}/location/${loc.id}`)}
                  style={{
                    background: '#fff', borderRadius: 10, padding: '12px 14px', marginBottom: 8,
                    display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 500, color: '#222' }}>{loc.location_name}</div>
                    <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
                      已盘 {locChecked} / {locTotal}
                      {Number(loc.unchecked_count) > 0 ? ` · 未盘 ${loc.unchecked_count}` : ''}
                    </div>
                  </div>
                  <Tag color={done ? 'green' : (locChecked > 0 ? 'blue' : 'default')}>
                    {done ? '已完成' : (locChecked > 0 ? '进行中' : '未开始')}
                  </Tag>
                </div>
              );
            })
          )}
        </div>

        {remarks.length > 0 && (
          <div style={{ padding: '0 16px 16px' }}>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 10, color: '#333' }}>
              资产备注
              {isSuperAdmin(user) && task?.status === 2 ? (
                <span style={{ fontSize: 12, fontWeight: 400, color: '#999', marginLeft: 8 }}>
                  待确认 {unconfirmed} / 共 {remarks.length}
                </span>
              ) : null}
            </div>
            {remarks.map((r) => (
              <div
                key={r.id}
                style={{
                  background: '#fff',
                  borderRadius: 10,
                  padding: 12,
                  marginBottom: 8,
                  border: r.confirmed ? '1px solid #b7eb8f' : '1px solid #ffe7ba',
                }}
              >
                <div style={{ fontSize: 13, color: '#666', marginBottom: 4 }}>
                  【{r.location_name || '未知房间'}】{r.asset_name}
                  {r.asset_code ? `（${r.asset_code}）` : ''}
                </div>
                <div style={{ fontSize: 14, color: '#222', lineHeight: 1.5 }}>{r.content}</div>
                <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Tag color={r.confirmed ? 'green' : 'orange'}>
                    {r.confirmed ? '已确认' : '待确认'}
                  </Tag>
                  {isSuperAdmin(user) && task?.status === 2 && !r.confirmed ? (
                    <Button
                      size="small"
                      type="primary"
                      loading={confirmingId === r.id}
                      onClick={() => handleConfirmRemark(r)}
                    >
                      确认
                    </Button>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        )}
      </Spin>

      {canComplete && (
        <div style={{
          position: 'fixed', left: 0, right: 0, bottom: H5_TAB_BAR_PX, padding: '10px 16px',
          background: '#fff', borderTop: '1px solid #f0f0f0', zIndex: 20,
        }}>
          <Button type="primary" block size="large" style={{ borderRadius: 8 }} onClick={openComplete}>
            提交盘点完成
          </Button>
        </div>
      )}

      <Modal
        title="盘点完成反馈"
        open={completeOpen}
        onCancel={() => setCompleteOpen(false)}
        onOk={handleComplete}
        confirmLoading={submitting}
        okText="提交给超管"
        destroyOnClose
      >
        <div style={{ marginBottom: 12, fontSize: 13, color: '#666' }}>
          当前进度：已盘 {checked}/{total}
          {remarks.length ? ` · 已有 ${remarks.length} 条资产备注` : ''}
        </div>
        {remarks.length > 0 && (
          <div style={{
            marginBottom: 12, padding: 10, background: '#fff7e6', borderRadius: 8,
            fontSize: 12, color: '#ad6800', whiteSpace: 'pre-wrap', maxHeight: 140, overflow: 'auto',
          }}
          >
            {remarkSummary || '资产备注将自动并入下方反馈'}
          </div>
        )}
        <Radio.Group
          value={feedbackOk}
          onChange={(e) => {
            const v = e.target.value;
            setFeedbackOk(v);
            if (v === 1) setFeedback((prev) => (prev && prev !== '全部符合' ? prev : '全部符合'));
            else if (feedback === '全部符合') setFeedback(remarkSummary || '');
          }}
          style={{ marginBottom: 12 }}
        >
          <Radio value={1}>全部符合</Radio>
          <Radio value={0}>存在问题</Radio>
        </Radio.Group>
        <Input.TextArea
          rows={6}
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          placeholder={feedbackOk === 1 ? '可补充说明' : '请说明哪些房间/资产存在问题'}
        />
      </Modal>
    </div>
  );
}
